import { useState } from "react";
import { toast } from "sonner";
import { Input } from "../ui/input";
import { Switch } from "../ui/switch";
import { api, errMsg, store } from "../../lib/api";

const CONSENTS = [
  ["specialist_referral", "I'd like an AskJoe specialist to review my situation."],
  ["document_sharing", "Share my Situation Report and uploaded documents with my designated specialist."],
  ["electronic_communications", "I agree to receive electronic communications about my case."],
  ["phone", "You may call me about my case."],
  ["sms", "You may text me about my case (message & data rates may apply)."],
  ["email", "You may email me about my case."],
  ["third_party_referral", "You may refer me to an outside professional (attorney, CPA, housing counselor)."],
  ["marketing", "Send me occasional marketing communications (optional)."],
];

export const Handoff = ({ data, onChange }) => {
  const [choice, setChoice] = useState(data.handoff?.choice === "yes" ? "yes" : null);
  const [f, setF] = useState({ name: "", phone: "", email: "", preferred_method: "phone", best_time: "" , ...(data.contact || {}) });
  const [c, setC] = useState({ specialist_referral: true });
  const [busy, setBusy] = useState(false);
  const done = data.handoff?.choice === "yes";

  const submit = async (ch) => {
    setBusy(true);
    try {
      await api.post("/case/handoff", ch === "yes" ? { choice: "yes", ...f, email: f.email || null, consents: c } : { choice: ch });
      toast.success(ch === "yes" ? "Thank you. A specialist will review your situation." : "Okay — keep going with Joe anytime.");
      onChange();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  if (done) return (
    <div data-testid="handoff-done" className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5">
      <p className="font-display text-lg font-bold text-emerald-900">Your specialist request was received.</p>
      <p className="mt-1 text-sm text-emerald-900">Recommended specialist category: <b>{data.analysis.specialists.map((s) => s.label).join(", ")}</b>. We'll contact you by {data.contact?.preferred_method}.</p>
    </div>
  );

  return (
    <div className="space-y-4" data-testid="handoff-panel">
      <h3 className="font-display text-xl font-bold">Would you like an AskJoe specialist to review your situation?</h3>
      <p className="text-sm text-slate-600">Recommended: {data.analysis.specialists.map((s) => s.label).join(" • ")}</p>
      <div className="grid gap-2 sm:grid-cols-3">
        {[["yes", "Yes"], ["not_yet", "Not yet"], ["keep_joe", "I want to keep using Joe"]].map(([v, l]) => (
          <button key={v} data-testid={`handoff-choice-${v}`} onClick={() => (v === "yes" ? setChoice("yes") : submit(v))}
            className={`tap h-12 rounded-xl border-2 font-bold uppercase tracking-wide ${choice === v ? "border-slate-900 bg-slate-900 text-white" : "border-slate-300 bg-white"}`}>{l}</button>
        ))}
      </div>
      {choice === "yes" && (
        <div className="rise space-y-3 rounded-2xl border border-slate-200 bg-white p-5">
          <div className="grid gap-2 sm:grid-cols-2">
            <Input data-testid="handoff-name" placeholder="Full name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="h-12" />
            <Input data-testid="handoff-phone" placeholder="Phone" value={f.phone || ""} onChange={(e) => setF({ ...f, phone: e.target.value })} className="h-12" />
            <Input data-testid="handoff-email" placeholder="Email" type="email" value={f.email || ""} onChange={(e) => setF({ ...f, email: e.target.value })} className="h-12" />
            <Input data-testid="handoff-best-time" placeholder="Best time to contact (e.g. weekdays after 5pm)" value={f.best_time || ""} onChange={(e) => setF({ ...f, best_time: e.target.value })} className="h-12" />
          </div>
          <div className="flex flex-wrap gap-2">
            <span className="self-center text-sm font-semibold text-slate-600">Preferred contact:</span>
            {["phone", "text", "email"].map((m) => (
              <button key={m} data-testid={`handoff-method-${m}`} onClick={() => setF({ ...f, preferred_method: m })} className={`tap rounded-full px-3 py-1 text-sm font-semibold ${f.preferred_method === m ? "bg-slate-900 text-white" : "bg-slate-100"}`}>{m}</button>
            ))}
          </div>
          <div className="space-y-2 pt-2">
            {CONSENTS.map(([k, l]) => (
              <label key={k} className="flex items-start justify-between gap-3 rounded-xl bg-slate-50 p-3 text-sm">
                <span>{l}</span><Switch data-testid={`consent-${k}`} checked={!!c[k]} onCheckedChange={(v) => setC({ ...c, [k]: v })} />
              </label>
            ))}
            <p className="text-xs text-slate-500">Each permission is recorded separately and can be changed later. Scanning a QR code or opening a link is never treated as consent to marketing.</p>
          </div>
          <button data-testid="handoff-submit-btn" disabled={busy} onClick={() => submit("yes")} className="tap h-12 w-full rounded-xl bg-slate-900 font-display font-bold uppercase tracking-wide text-white">Request specialist review</button>
        </div>
      )}
    </div>
  );
};

export const AccountCard = ({ onDone }) => {
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/case/account", { email, password: pw });
      store.token = data.token;
      store.caseToken = null;
      toast.success("Your case is now protected by your password.");
      onDone();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <div data-testid="account-card" className="rounded-2xl border border-orange-200 bg-orange-50 p-5">
      <p className="font-display text-lg font-bold uppercase text-brand-navy">Protect your case &amp; save your progress</p>
      <p className="mt-1 text-sm text-slate-600">Create a password so only you can return to your financial information and documents.</p>
      <div className="mt-3 grid gap-2 sm:grid-cols-[1fr_1fr_auto]">
        <Input data-testid="account-email" type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} className="h-11 bg-white" />
        <Input data-testid="account-password" type="password" placeholder="Password (8+ characters)" value={pw} onChange={(e) => setPw(e.target.value)} className="h-11 bg-white" />
        <button data-testid="account-create-btn" disabled={busy} onClick={go} className="tap h-11 rounded-xl bg-brand-orange px-5 font-bold text-white">Save</button>
      </div>
    </div>
  );
};
